var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _onVariantChange, _pendingRequestUrl, _abortController, _checkedIndices, _radios, _resizeObserver, _VariantPicker_instances, getFieldsetMeasurements_fn, applyFieldsetMeasurements_fn;
import { Component } from "@theme/component";
import { morph, MORPH_OPTIONS } from "@theme/morph";
import { OverflowList } from "@theme/overflow-list";
import { fetchWithDeadline, yieldToMainThread, getViewParameterValue, ResizeNotifier } from "@theme/utilities";
import { ProductSelectEvent } from "@shopify/events";
class VariantPicker extends Component {
  constructor() {
    super(...arguments);
    this.selectionUrlGeneration = 0;
    __privateAdd(this, _VariantPicker_instances);
    __privateAdd(this, _onVariantChange, this.variantChanged.bind(this));
    __privateAdd(this, _pendingRequestUrl);
    __privateAdd(this, _abortController);
    __privateAdd(this, _checkedIndices, []);
    __privateAdd(this, _radios, []);
    __privateAdd(this, _resizeObserver, new ResizeNotifier(() => this.updateVariantPickerCss()));
  }
  connectedCallback() {
    super.connectedCallback();
    __privateSet(this, _radios, []);
    __privateSet(this, _checkedIndices, []);
    const fieldsets = (
      /** @type {HTMLFieldSetElement[]} */
      this.refs.fieldsets || []
    );
    fieldsets.forEach((fieldset) => {
      var _a;
      const radios = Array.from((_a = fieldset == null ? void 0 : fieldset.querySelectorAll("input")) != null ? _a : []);
      __privateGet(this, _radios).push(radios);
      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === "true");
      if (initialCheckedIndex !== -1) {
        __privateGet(this, _checkedIndices).push([initialCheckedIndex]);
      }
    });
    this.addEventListener("change", __privateGet(this, _onVariantChange));
    __privateGet(this, _resizeObserver).observe(this);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    ++this.selectionUrlGeneration;
    __privateGet(this, _resizeObserver).disconnect();
    this.removeEventListener("change", __privateGet(this, _onVariantChange));
    const active = __privateGet(this, _abortController);
    if (active) active.abort();
  }
  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    var _a, _b;
    if (!(event.target instanceof HTMLElement)) return;
    const selectedOption = event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;
    if (!selectedOption) return;
    const urlGeneration = ++this.selectionUrlGeneration;
    this.updateSelectedOption(event.target);
    const isOnProductPage = this.dataset.templateProductMatch === "true" && !event.target.closest("product-card") && !event.target.closest("quick-add-dialog");
    const currentUrl = (_a = this.dataset.productUrl) == null ? void 0 : _a.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest("featured-product-information"));
    const morphElementSelector = loadsNewProduct ? "main" : isOnFeaturedProductSection ? "featured-product-information" : void 0;
    const optionValueId = (_b = selectedOption.dataset.optionValueId) != null ? _b : "";
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, optionValueId);
    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;
    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }
    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        if (this.isConnected && urlGeneration === this.selectionUrlGeneration) history.replaceState({}, "", url.toString());
      });
    }
  }
  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;
    const measurements = __privateMethod(this, _VariantPicker_instances, getFieldsetMeasurements_fn).call(this, fieldsetIndex);
    if (measurements) {
      __privateMethod(this, _VariantPicker_instances, applyFieldsetMeasurements_fn).call(this, measurements);
    }
  }
  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector('[data-option-value-id="'.concat(target, '"]'));
      if (!targetElement) throw new Error("Target element not found");
      target = targetElement;
    }
    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || "");
      const inputIndex = Number.parseInt(target.dataset.inputIndex || "");
      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = (
          /** @type {HTMLFieldSetElement[]} */
          this.refs.fieldsets || []
        );
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = __privateGet(this, _checkedIndices)[fieldsetIndex];
        const radios = __privateGet(this, _radios)[fieldsetIndex];
        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;
          if (currentIndex !== void 0 && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = "false";
          }
          if (previousIndex !== void 0 && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = "false";
          }
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);
          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];
          if (newCurrentIndex !== void 0 && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = "true";
          }
          if (newPreviousIndex !== void 0 && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = "true";
            radios[newPreviousIndex].dataset.currentChecked = "false";
          }
          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error("Option not found");
      for (const option of target.options) {
        option.removeAttribute("selected");
      }
      newSelectedOption.setAttribute("selected", "selected");
    }
  }
  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    var _a;
    let productUrl = selectedOption.dataset.connectedProductUrl || __privateGet(this, _pendingRequestUrl) || this.dataset.productUrl;
    __privateSet(this, _pendingRequestUrl, productUrl);
    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push("view=".concat(viewParamValue));
    if (this.selectedOptionsValues.length && !source) {
      params.push("option_values=".concat(this.selectedOptionsValues.join(",")));
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push("option_values=".concat(sourceSelectedOptionsValues.join(",")));
      } else {
        params.push("option_values=".concat(selectedOption.dataset.optionValueId));
      }
    }
    const SECTION_ID_MAP = {
      "quick-add-component": "section-rendering-product-card",
      "swatches-variant-picker-component": "section-rendering-product-card",
      "featured-product-information": (_a = this.closest("featured-product-information")) == null ? void 0 : _a.id
    };
    const closestSectionId = (
      /** @type {keyof typeof SECTION_ID_MAP} | undefined */
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );
    if (closestSectionId) {
      if (productUrl == null ? void 0 : productUrl.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return "".concat(productUrl, "?section_id=").concat(SECTION_ID_MAP[closestSectionId], "&").concat(params.join("&"));
    }
    return "".concat(productUrl, "?").concat(params.join("&"));
  }
  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {string} [optionValueId] - The selected option value ID for event detail.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, optionValueId = "") {
    var _a, _b, _c, _d;
    (_a = __privateGet(this, _abortController)) == null ? void 0 : _a.abort();
    const controller = new AbortController();
    __privateSet(this, _abortController, controller);
    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();
    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: (_b = this.dataset.productId) != null ? _b : "",
          title: (_c = this.dataset.productTitle) != null ? _c : "",
          handle: (_d = this.dataset.productHandle) != null ? _d : ""
        },
        selectedOptions,
        detail: {
          optionValueId
        },
        promise: deferredEventPromise.promise
      })
    );
    return fetchWithDeadline(requestUrl, {}, controller, 15e3, (response) => {
      if (!response.ok) throw new Error("Variant request failed: ".concat(response.status));
      return response.text();
    }).then((responseText) => {
      var _a3, _b3, _c3, _d3;
      if (controller.signal.aborted) {
        const error = new Error("Variant request cancelled");
        error.name = "AbortError";
        throw error;
      }
      var _a2, _b2, _c2, _d2, _e, _f, _g, _h, _i;
      __privateSet(this, _pendingRequestUrl, void 0);
      const html = new DOMParser().parseFromString(responseText, "text/html");
      (_a2 = html.querySelector("overflow-list[defer]")) == null ? void 0 : _a2.removeAttribute("defer");
      const variantPickerJsonScript = html.querySelector('variant-picker script[type="application/json"]');
      const textContent = variantPickerJsonScript == null ? void 0 : variantPickerJsonScript.textContent;
      if (!textContent) {
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: (_b2 = this.dataset.productId) != null ? _b2 : "",
            sourceId: this.selectedOptionId,
            resource: null
          }
        });
        return;
      }
      const variantData = JSON.parse(textContent);
      if (variantData !== null && (typeof variantData !== "object" || Array.isArray(variantData))) throw new Error("Invalid variant data");
      if (variantData && (!Number.isSafeInteger(Number(variantData.id)) || Number(variantData.id) <= 0 || typeof variantData.available !== "boolean")) throw new Error("Invalid variant data");
      const productViewPayload = (_b3 = (_a3 = variantPickerJsonScript.closest("[view-event-payload]")) == null ? void 0 : _a3.getAttribute("view-event-payload")) == null ? void 0 : _b3.trim();
      const eventVariant = productViewPayload ? ((_d3 = (_c3 = JSON.parse(productViewPayload)) == null ? void 0 : _c3.product) == null ? void 0 : _d3.selectedVariant) || null : null;
      let newProduct;
      if (morphElementSelector === "main") {
        this.updateMain(html);
      } else if (morphElementSelector) {
        this.updateElement(html, morphElementSelector);
      } else {
        const { overflowList } = this.refs;
        const wasSwatchesExpanded = overflowList instanceof OverflowList && overflowList.getAttribute("disabled") === "true";
        newProduct = this.updateVariantPicker(html);
        if (wasSwatchesExpanded) {
          const overflowListAfterMorph = overflowList;
          if (overflowListAfterMorph instanceof OverflowList) {
            overflowListAfterMorph.showAll();
          }
        }
      }
      if (this.selectedOptionId) {
        if (variantData && typeof variantData === "object") {
          deferredEventPromise.resolve({
            variant: eventVariant,
            detail: {
              html,
              productId: (_h = this.dataset.productId) != null ? _h : "",
              newProduct,
              sourceId: this.selectedOptionId,
              resource: variantData
            }
          });
          return;
        }
      }
      deferredEventPromise.resolve({
        variant: null,
        detail: {
          html,
          productId: (_i = this.dataset.productId) != null ? _i : "",
          newProduct,
          sourceId: this.selectedOptionId,
          resource: null
        }
      });
    }).catch((error) => {
      deferredEventPromise.reject(error);
      if (error.name === "AbortError") {
        console.warn("Fetch aborted by user");
      } else {
        console.error(error);
      }
    }).finally(() => {
      if (__privateGet(this, _abortController) === controller) {
        __privateSet(this, _pendingRequestUrl, void 0);
      }
    });
  }
  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */
  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    let newProduct;
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;
      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }
      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }
    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return void 0;
        const key = node.dataset.key;
        return key;
      }
    });
    this.updateVariantPickerCss();
    return newProduct;
  }
  updateVariantPickerCss() {
    const fieldsets = (
      /** @type {HTMLFieldSetElement[]} */
      this.refs.fieldsets || []
    );
    const measurements = fieldsets.map((_, index) => __privateMethod(this, _VariantPicker_instances, getFieldsetMeasurements_fn).call(this, index)).filter((m) => m !== null);
    for (const measurement of measurements) {
      __privateMethod(this, _VariantPicker_instances, applyFieldsetMeasurements_fn).call(this, measurement);
    }
  }
  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);
    if (!element || !newElement) {
      throw new Error("No new element source found for ".concat(elementSelector));
    }
    morph(element, newElement);
  }
  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");
    if (!main || !newMain) {
      throw new Error("No new main source found");
    }
    morph(main, newMain);
  }
  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector("select option[selected], fieldset input:checked");
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return void 0;
    }
    return selectedOption;
  }
  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    var _a, _b;
    const options = [];
    for (const select of this.querySelectorAll("select")) {
      const selected = select.selectedOptions[0];
      if ((_a = selected == null ? void 0 : selected.dataset) == null ? void 0 : _a.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }
    const checkedInputs = this.querySelectorAll("fieldset input:checked");
    for (const input of checkedInputs) {
      if ((_b = input.dataset) == null ? void 0 : _b.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }
    return options;
  }
  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return void 0;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) {
      throw new Error("No option value ID found");
    }
    return optionValueId;
  }
  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll("select option[selected], fieldset input:checked"));
    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error("No option value ID found");
      return optionValueId;
    });
  }
}
_onVariantChange = /* @__PURE__ */ new WeakMap();
_pendingRequestUrl = /* @__PURE__ */ new WeakMap();
_abortController = /* @__PURE__ */ new WeakMap();
_checkedIndices = /* @__PURE__ */ new WeakMap();
_radios = /* @__PURE__ */ new WeakMap();
_resizeObserver = /* @__PURE__ */ new WeakMap();
_VariantPicker_instances = /* @__PURE__ */ new WeakSet();
getFieldsetMeasurements_fn = function(fieldsetIndex) {
  var _a, _b, _c, _d;
  const fieldsets = (
    /** @type {HTMLFieldSetElement[]} */
    this.refs.fieldsets || []
  );
  const fieldset = fieldsets[fieldsetIndex];
  const checkedIndices = __privateGet(this, _checkedIndices)[fieldsetIndex];
  const radios = __privateGet(this, _radios)[fieldsetIndex];
  if (!radios || !checkedIndices || !fieldset) return null;
  const [currentIndex, previousIndex] = checkedIndices;
  return {
    fieldset,
    currentIndex,
    previousIndex,
    currentWidth: currentIndex !== void 0 ? (_b = (_a = radios[currentIndex]) == null ? void 0 : _a.parentElement) == null ? void 0 : _b.offsetWidth : void 0,
    previousWidth: previousIndex !== void 0 ? (_d = (_c = radios[previousIndex]) == null ? void 0 : _c.parentElement) == null ? void 0 : _d.offsetWidth : void 0
  };
};
applyFieldsetMeasurements_fn = function({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
  if (currentWidth) {
    fieldset.style.setProperty("--pill-width-current", "".concat(currentWidth, "px"));
  } else if (currentIndex !== void 0) {
    fieldset.style.removeProperty("--pill-width-current");
  }
  if (previousWidth) {
    fieldset.style.setProperty("--pill-width-previous", "".concat(previousWidth, "px"));
  } else if (previousIndex !== void 0) {
    fieldset.style.removeProperty("--pill-width-previous");
  }
};
if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
export {
  VariantPicker as default
};
